/**
 * Canonical set of `field` names the manifest cross-source comparators can emit
 * as a discrepancy row for the document-review feature (`compareManifestVsVendorFields`,
 * `compareManifestVsDocumentFields`, and the box-13/box-9 consistency check).
 *
 * Single source of truth shared by two packages that must not drift:
 *  - encamp-waste-data-access: the comparators emit these (a test asserts the
 *    emitted set stays a subset of this list).
 *  - oak: the review form's field maps translate these to inputs, with a
 *    COMPILE-TIME check that every field here is either mapped or explicitly
 *    excluded — so a new comparator field can't be silently dropped.
 *
 * Deliberately NOT here: `quantityUnitOfMeasureCode`. No comparator emits it —
 * WLU normalizes quantity to pounds, so unit-of-measure mismatches are noise
 * (see compareManifestVsDocumentFields). A field only belongs here if a
 * comparator can actually produce it.
 */
export const MANIFEST_REVIEW_DISCREPANCY_FIELDS = [
  // Manifest-level (EPA Form 8700-22 header).
  "generatorName",
  "generatorId",
  "generatorStreetAddress",
  "generatorStreet2",
  "generatorCity",
  "generatorState",
  "generatorZip",
  "shippedDate",
  "destinationFacilityId",
  "wasteLinesCount",
  // Per-waste-line.
  "quantity",
  "containerTypeCode",
  "managementMethodCode",
  "federalWasteCodes",
  "stateWasteCodes",
  "dotPrintedInformation",
  "nonHazWasteDescription",
  // Whole-line / single-source signals (not field-level form inputs).
  "unpairedWasteLine",
  "wasteCodeDotConsistency",
] as const;

export type ManifestReviewDiscrepancyField = (typeof MANIFEST_REVIEW_DISCREPANCY_FIELDS)[number];
